import type {
  ColDef,
  ColGroupDef,
  GridApi,
  GridState,
} from 'ag-grid-community';

export type TablePersistedState = 'full' | 'columnLayout';

export const resolveFitColumnsOnGridSizeChanged = ({
  fitColumnsOnGridSizeChanged,
  hasPersistence,
}: {
  fitColumnsOnGridSizeChanged?: boolean;
  hasPersistence: boolean;
}): boolean => fitColumnsOnGridSizeChanged ?? !hasPersistence;

export const resolveTablePersistenceStateUpdateGate = ({
  isRestoring,
  initializationComplete,
  hasRestoredState,
  hasAppliedPersistedState,
}: {
  isRestoring: boolean;
  initializationComplete: boolean;
  hasRestoredState: boolean;
  hasAppliedPersistedState: boolean;
}): {
  skipSave: boolean;
  markInitializationComplete: boolean;
} => {
  if (isRestoring) {
    return { skipSave: true, markInitializationComplete: false };
  }

  if (!initializationComplete) {
    const hasPendingPersistedRestore =
      hasRestoredState && !hasAppliedPersistedState;

    return {
      skipSave: true,
      markInitializationComplete: !hasPendingPersistedRestore,
    };
  }

  return { skipSave: false, markInitializationComplete: false };
};

export const resolvePersistedGridState = ({
  state,
  persistedState,
}: {
  state: GridState;
  persistedState: TablePersistedState;
}): GridState =>
  persistedState === 'columnLayout'
    ? {
        columnOrder: state.columnOrder,
        columnSizing: state.columnSizing,
        partialColumnState: true,
      }
    : state;

export const resolveOwnedLayoutToRestore = ({
  pendingLayout,
  pendingLayoutPersistenceKey,
  currentPersistenceKey,
  restoredState,
}: {
  pendingLayout?: GridState;
  pendingLayoutPersistenceKey: string | null;
  currentPersistenceKey: string;
  restoredState?: GridState;
}): GridState | undefined => {
  if (pendingLayoutPersistenceKey !== currentPersistenceKey) {
    return restoredState;
  }

  return pendingLayout ?? restoredState;
};

export const shouldSkipPersistedGridStateApply = ({
  appliedPersistedStateKey,
  nextPersistedStateKey,
  columnDefsChanged,
}: {
  appliedPersistedStateKey: string | null;
  nextPersistedStateKey: string;
  columnDefsChanged: boolean;
}): boolean => {
  if (columnDefsChanged) {
    return false;
  }

  return appliedPersistedStateKey === nextPersistedStateKey;
};

export const buildTablePersistenceIdentityKey = ({
  tableId,
  applicationId,
  userId,
  gridKey,
}: {
  tableId?: string;
  applicationId?: string;
  userId?: string;
  gridKey?: string;
}): string =>
  [tableId ?? '', applicationId ?? '', userId ?? '', gridKey ?? ''].join('\0');

const isColumnGroupDef = <TData>(
  columnDef: ColDef<TData> | ColGroupDef<TData>,
): columnDef is ColGroupDef<TData> =>
  Array.isArray((columnDef as ColGroupDef<TData>).children);

type PersistedColumnSizingEntry = {
  colId: string;
  width?: number;
  flex?: number;
};

export const applyPersistedPixelWidthsToColumnDefs = <TData>({
  columnDefs,
  columnSizingModel,
}: {
  columnDefs: (ColDef<TData> | ColGroupDef<TData>)[] | null | undefined;
  columnSizingModel?: PersistedColumnSizingEntry[];
}): (ColDef<TData> | ColGroupDef<TData>)[] | null | undefined => {
  if (!columnDefs?.length || !columnSizingModel?.length) {
    return columnDefs;
  }

  const persistedPixelWidths = new Map(
    columnSizingModel
      .filter((entry) => entry.width != null && entry.flex == null)
      .map((entry) => [entry.colId, entry.width as number]),
  );

  if (!persistedPixelWidths.size) {
    return columnDefs;
  }

  return columnDefs.map((columnDef) => {
    if (isColumnGroupDef(columnDef)) {
      return {
        ...columnDef,
        children:
          applyPersistedPixelWidthsToColumnDefs({
            columnDefs: columnDef.children,
            columnSizingModel,
          }) ?? [],
      };
    }

    const colId = columnDef.colId ?? columnDef.field;

    if (!colId || !persistedPixelWidths.has(colId)) {
      return columnDef;
    }

    const { flex: _flex, ...columnDefWithoutFlex } = columnDef;

    return {
      ...columnDefWithoutFlex,
      width: persistedPixelWidths.get(colId),
    };
  });
};

type ApplyGridStateApi = Pick<GridApi, 'applyColumnState' | 'setFilterModel'>;

export function applyGridStateToApi({
  api,
  state,
  persistedState,
}: {
  api: ApplyGridStateApi;
  state: GridState;
  persistedState: TablePersistedState;
}) {
  const orderedColIds =
    state.columnOrder?.orderedColIds ??
    state.columnSizing?.columnSizingModel?.map((entry) => entry.colId) ??
    [];

  if (!orderedColIds.length) return;
  const sizingMap = new Map(
    state.columnSizing?.columnSizingModel?.map((s) => [s.colId, s]),
  );
  const hiddenSet = new Set(state.columnVisibility?.hiddenColIds);
  const leftPinned = new Set(state.columnPinning?.leftColIds);
  const rightPinned = new Set(state.columnPinning?.rightColIds);
  const sortMap = new Map(
    state.sort?.sortModel?.map((s, idx) => [
      s.colId,
      { sort: s.sort, sortIndex: idx },
    ]),
  );
  const isFullState = persistedState === 'full';
  const columnStates = orderedColIds.map((colId) => {
    const sizing = sizingMap.get(colId);
    const sortEntry = sortMap.get(colId);
    const hasWidth = sizing?.width != null;
    const hasFlex = sizing?.flex != null;

    return {
      colId,
      ...(hasWidth && { width: sizing.width }),
      ...(hasWidth || hasFlex ? { flex: hasFlex ? sizing.flex : null } : {}),
      ...(isFullState && {
        hide: hiddenSet.has(colId),
        pinned: leftPinned.has(colId)
          ? ('left' as const)
          : rightPinned.has(colId)
            ? ('right' as const)
            : null,
      }),
      ...(sortEntry && {
        sort: sortEntry.sort,
        sortIndex: sortEntry.sortIndex,
      }),
    };
  });

  api.applyColumnState({ state: columnStates, applyOrder: true });
  if (isFullState && state.filter?.filterModel) {
    api.setFilterModel(state.filter.filterModel);
  }
}
